import { useEffect, useState, useMemo } from 'react';
import { useInventoryTransferQuery } from '../../../hooks/inventory/useInventoryTransferQuery';
import { useBranch } from '../../../hooks/branches/useBranch';
import { useProduct } from '../../../hooks/useProduct';
import AdvancedRecordTable from '../../Tables/advancedRecord/AdvancedRecordTable';
import { FilterConfig } from '../../Tables/advancedRecord/FilterBar';
import { InventoryTransferTableDTO } from '../../../types/inventory/transfer/InventoryTransferTypes';
import { transferStatusOptions } from '../../../constants/inventory/transferStatusOptions';
import { inventoryTransferColumns } from '../../../features/inventory/transfer/inventoryTransferColumns';
import { buildInventoryTransferFilters } from '../../../features/inventory/transfer/buildInventoryTransferFilters';
import PendingTransferModal from './Modals/PendingTransferModal';
import { useInventoryTransfer } from '../../../hooks/inventory/useInventoryTransfer';
import InventoryTransferEditModal from './Modals/TransferEdit/InventoryTransferEditModal';
import InventoryTransferDetailModal from './Modals/TransferDetail/InventoryTransferDetailModal';
import { CreateInventoryTransferModal } from './Modals/Create/CreateInventoryTransferModal';
import InventoryTransferApproveModal from './Modals/Approve/InventoryTransferApproveModal';
import InventoryTransferConfirmModal from './Modals/Confirm/InventoryTransferConfirmModal';
import InventoryTransferCancelModal from './Modals/Cancel/InventoryTransferCancelModal';
import InventoryTransferReverseModal from './Modals/Reverse/InventoryTransferReverseModal';
import InventoryTransferRejectModal from './Modals/Reject/InventoryTransferRejectModal';
import { useAuthUser } from '../../../hooks/auth/useAuthUser';

const InventoryTransferTableSection: React.FC = () => {
  const {
    list,
    paginator,
    pendingCount,
    handlerOpenPendingModal,
    handlerClosePendingModal,
    showPendingTransferModal,
    handlerGetFilteredTransfers,
    handlerGetPendingCount,
    handlerClearTransfers,
    handlerGetTransferById,
  } = useInventoryTransferQuery();

  const { fetchBranchDropdown, branchDropdown } = useBranch();
  const { handlerGetProductsForDropdown, productsDropdown } = useProduct();
  const { openCreateModal, handlerOpen } = useInventoryTransfer();
  const { hasRole } = useAuthUser();

  const [filters, setFilters] = useState({});
  const [filtersApplied, setFiltersApplied] = useState(false);

  const pageSize = paginator?.size ?? 10;
  const currentPage = paginator?.number ?? 0;

  // ------------------------------------------------------
  // Cargar catálogos solo una vez
  // ------------------------------------------------------
  useEffect(() => {
    fetchBranchDropdown();
    handlerGetProductsForDropdown();
    handlerGetPendingCount();
  }, []);

  // ------------------------------------------------------
  // Columnas
  // ------------------------------------------------------
  const columns = useMemo(() => inventoryTransferColumns, []);

  // ------------------------------------------------------
  // Filtros
  // ------------------------------------------------------
  const filterFields: FilterConfig[] = useMemo(
    () =>
      buildInventoryTransferFilters(
        branchDropdown,
        productsDropdown,
        transferStatusOptions
      ),
    [branchDropdown, productsDropdown]
  );

  // ------------------------------------------------------
  // Payload
  // ------------------------------------------------------
  const buildTransferFilterPayload = (rawFilters: any) => {
    const payload: any = {};

    if (rawFilters.branchId) payload.branchId = Number(rawFilters.branchId);
    if (rawFilters.productId) payload.productId = Number(rawFilters.productId);
    if (rawFilters.status) payload.status = rawFilters.status;

    if (rawFilters.dateFrom)
      payload.dateFrom = `${rawFilters.dateFrom}T00:00:00`;

    if (rawFilters.dateTo) payload.dateTo = `${rawFilters.dateTo}T23:59:59`;

    return payload;
  };

  const handleFilterChange = (newFilters: any) => {
    const payload = buildTransferFilterPayload(newFilters);
    setFilters(payload);
    setFiltersApplied(true);
    handlerGetFilteredTransfers(payload, 0, pageSize);
  };

  const handlePageChange = (page: number) => {
    handlerGetFilteredTransfers(filters, page, pageSize);
  };

  const handlePageSizeChange = (size: number) => {
    handlerGetFilteredTransfers(filters, 0, size);
  };

  useEffect(() => {
    if (filtersApplied) {
      handlerGetFilteredTransfers(filters, currentPage, pageSize);
    }
  }, [currentPage]);

  const getActions = (row: InventoryTransferTableDTO) => {
    const actions = [];

    // 👁 Todos pueden ver detalle
    actions.push({
      key: 'view',
      label: 'Ver detalle',
      onClick: () => handlerGetTransferById(row.id),
    });

    // ==========================================
    // 🛡 ADMIN y SUPPORT → Revertir & Rechazar
    // ==========================================
    if (hasRole('ADMIN') || hasRole('SUPPORT')) {
      actions.push(
        {
          key: 'reverse',
          label: 'Revertir',
          onClick: () => handlerOpen(row.id, 'reverse'),
        },
        {
          key: 'reject',
          label: 'Rechazar',
          onClick: () => handlerOpen(row.id, 'reject'),
        }
      );
    }

    return actions;
  };

  // ------------------------------------------------------
  // Render principal
  // ------------------------------------------------------
  return (
    <>
      <AdvancedRecordTable
        columns={columns}
        data={list}
        onPageChange={handlePageChange}
        onPageSizeChange={handlePageSizeChange}
        filtersConfig={filterFields}
        onFilterChange={handleFilterChange}
        onResetFilters={handlerClearTransfers}
        paginator={paginator}
        pageSize={pageSize}
        url="/inventory/transfers"
        noDataMessage="No hay traslados. Ajusta los filtros para refinar tu búsqueda."
        topLeftActions={[
          {
            label: 'Traslados pendientes',
            onClick: handlerOpenPendingModal,
            count: pendingCount,
          },
        ]}
        topRightAction={{
          label: 'Nuevo Traslado',
          onClick: openCreateModal,
        }}
        getActions={getActions}
      />

      <PendingTransferModal
        isOpen={showPendingTransferModal}
        onClose={handlerClosePendingModal}
      />

      <CreateInventoryTransferModal />
      <InventoryTransferDetailModal />
      <InventoryTransferEditModal />
      <InventoryTransferApproveModal />
      <InventoryTransferConfirmModal />
      <InventoryTransferCancelModal />
      <InventoryTransferRejectModal />
      <InventoryTransferReverseModal />
    </>
  );
};

export default InventoryTransferTableSection;
